import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { InfiniteLoadingBar, ProgressLoadingBar, toKb } from './utils'
import clsx from 'clsx'
import { FileUploaderContext } from './FileUploader'

const Popover = ({ top, left, width, text }) => {
  useEffect(() => {
    const hasExistedPreventScroll = document.body.classList.contains('overlay-activated')
    if (hasExistedPreventScroll) return

    document.body.classList.toggle('overlay-activated', true)
    return () => {
      document.body.classList.toggle('overlay-activated', false)
    }
  }, [])

  return (
    <div style={{ top, left, width }} className="file-item-label-popover">
      <span>{text}</span>
    </div>
  )
}

export const LabelName = ({ text, wrapperRef }) => {
  const ref = useRef()
  const [show, setShow] = useState(false)

  return (
    <div className="file-item-label-container">
      <div
        ref={ref}
        onMouseLeave={() => setShow(false)}
        onMouseOver={() => setShow(ref.current.offsetWidth !== ref.current.scrollWidth)}
        className="dome-cursor-pointer file-item-label-text dome-flex-1 dome-p2 dome-color-darkest-grey"
      >
        {text}
      </div>
      {show && (
        <Popover
          text={text}
          top={ref.current.getBoundingClientRect().top + ref.current.getBoundingClientRect().height}
          left={ref.current.getBoundingClientRect().left - 8}
          width={(wrapperRef.current?.offsetWidth || 0) + 16}
        />
      )}
    </div>
  )
}

const Label = ({ fileName, id, onSearch }) => {
  const wrapperRef = useRef()
  const { removeFile } = useContext(FileUploaderContext)

  return (
    <div
      className="file-item-label-container-wrapper dome-relative dome-d-flex dome-gap12 dome-align-center"
      ref={wrapperRef}
    >
      <LabelName text={fileName} wrapperRef={wrapperRef} />
      {onSearch && (
        <span onClick={onSearch} className="dome-cursor-pointer">
          <i className="fa-light fa-magnifying-glass"></i>
        </span>
      )}
      <span onClick={() => removeFile(id)} className="dome-cursor-pointer">
        <i style={{ paddingRight: '6px' }} className="fa-light fa-xmark dome-color-red-alert"></i>
      </span>
    </div>
  )
}

export function FileItemUploading({ fileName, id, progress, progressLabel }) {
  return (
    <div className="file-item dome-d-flex dome-flex-column">
      <Label fileName={fileName} id={id} />
      <div className="dome-color-dark-grey progress-label">{progressLabel}</div>
      <ProgressLoadingBar progress={progress} />
    </div>
  )
}

export function FileItemProcessing({ fileName, id, progressLabel }) {
  return (
    <div className="file-item dome-d-flex dome-flex-column">
      <Label fileName={fileName} id={id} />
      <div className="dome-color-dark-grey progress-label">{progressLabel}</div>
      <InfiniteLoadingBar />
    </div>
  )
}

export function FileItemSuccess({ fileName, id, fileSize, fileUrl }) {
  const onSearch = useCallback(() => {
    if (!fileUrl) return

    window.open(fileUrl, '_blank')
  }, [fileUrl])

  return (
    <div className="file-item dome-d-flex dome-flex-column">
      <Label fileName={fileName} id={id} onSearch={onSearch} />
      <div className="file-item-success dome-d-flex dome-gap6 dome-align-center">
        <span>
          <i className="dome-color-sage fa-sharp fa-regular fa-circle-check"></i>
        </span>
        <span className="dome-color-dark-grey">{toKb(fileSize)}</span>
      </div>
    </div>
  )
}

export function FileItemDeleted({ fileName, id }) {
  return (
    <div className="file-item dome-d-flex dome-flex-column dome-gap6">
      <span className="dome-p2 file-item-label-text dome-color-disabled">{fileName}</span>
      <span className="dome-p3 dome-color-red-alert">File deleted.</span>
    </div>
  )
}

export function FileItemError({ fileName, id, error = '' }) {
  return (
    <div className="file-item dome-d-flex dome-flex-column">
      <Label fileName={fileName} id={id} />
      <div className="file-item-error">
        <span>
          <i className="fa-solid dome-color-red-alert fa-circle-exclamation"></i>
        </span>
        <span className="dome-p3 dome-color-red-alert">{error || 'failed to process'}</span>
      </div>
    </div>
  )
}

export function FileItemPasswordRequired({ fileName, id, status = '', value, onSubmit }) {
  return (
    <div className="file-item-password-required dome-d-flex dome-flex-column">
      <div className="file-item-password-required-content dome-d-flex dome-flex-column dome-gap3">
        <Label fileName={fileName} id={id} />
        <PasswordRequireStatus status={status} />
      </div>
      {status !== 'success' && <PasswordInputField onSubmit={onSubmit} password={value} />}
    </div>
  )
}

const PasswordRequireStatus = ({ status }) => {
  switch (status) {
    case 'success':
      return (
        <div style={{ marginTop: '9px' }} className="dome-d-flex dome-gap3 dome-align-center">
          <i className="dome-color-dark-green fa-sharp fa-regular fa-circle-check"></i>
          <span className="dome-p3 dome-color-dark-green dome-text-w500">success!</span>
        </div>
      )
    case 'error':
      return <span className="dome-p3 dome-color-red-alert">Password does not match. Try again.</span>
    default:
      return <span className="dome-p3 dome-color-aqua">password required</span>
  }
}

const PasswordInputField = ({ onSubmit, password }) => {
  const [value, setValue] = useState(password)
  const [isVisible, setIsVisible] = useState(false)

  const handleTogglePasswordVisibility = () => setIsVisible(!isVisible)
  const handleOnChange = (e) => setValue(e.target.value)
  const handleOnSubmit = (e) => onSubmit(value)

  return (
    <div className="dome-d-flex">
      <div className="file-item-password-required-input">
        <input
          value={value}
          onChange={handleOnChange}
          type="text"
          placeholder="enter password"
          className={clsx('dome-p3', !isVisible && 'dome-text-security')}
        />
        <i
          onClick={handleTogglePasswordVisibility}
          className={clsx('dome-color-aqua', isVisible ? 'fa-light fa-eye-slash' : 'fa-light fa-eye')}
        />
      </div>
      <button onClick={handleOnSubmit} disabled={!value} className="file-item-password-required-submit">
        <i className="fa-solid fa-paper-plane-top file-item-password-required-submit-icon"></i>
      </button>
    </div>
  )
}
